import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { syncCompanyFromSalesforce } from "@/api/syncCompanyFromSalesforce/syncCompanyFromSalesforceApi";
import type { SyncCompanyFromSalesforceRequest } from "@/api/syncCompanyFromSalesforce/types";
import { useNotification } from "@/context/NotificationContext";
import {
  syncCompanyFromSalesforceSchema,
  type SyncCompanyFromSalesforceFormData,
} from "../../schemas/companySchemas";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon, Loader2 } from "lucide-react";

type IdentifierType = "intacctId" | "salesforceId";

const CreateCompany: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [identifierType, setIdentifierType] =
    useState<IdentifierType>("salesforceId");

  const form = useForm<SyncCompanyFromSalesforceFormData>({
    resolver: zodResolver(syncCompanyFromSalesforceSchema),
    defaultValues: {
      intacctId: "",
      salesforceId: "",
    },
    mode: "onChange",
  });

  const { handleSubmit, formState } = form;
  const { isSubmitting, isValid } = formState;

  const handleTypeChange = (type: IdentifierType) => {
    setIdentifierType(type);
    if (type === "intacctId") {
      form.setValue("salesforceId", "", { shouldValidate: true });
    } else {
      form.setValue("intacctId", "", { shouldValidate: true });
    }
  };

  const onSubmit = async (values: SyncCompanyFromSalesforceFormData) => {
    const trimmedIntacctId = (values.intacctId ?? "").trim();
    const trimmedSalesforceId = (values.salesforceId ?? "").trim();
    const payload: SyncCompanyFromSalesforceRequest =
      identifierType === "intacctId"
        ? { intacctId: trimmedIntacctId }
        : { salesforceId: trimmedSalesforceId };

    try {
      const response = await syncCompanyFromSalesforce(payload);
      const newCompany = response.data;
      showNotification(
        "Company Synced",
        `"${newCompany.companyName}" has been synced from Salesforce. You can now configure additional settings.`,
        "success",
      );
      navigate(`/admin/companies/${newCompany.uuid}/edit`);
    } catch {
      // Errors are handled globally by the axios interceptor
    }
  };

  const isLoading = isSubmitting;

  return (
    <MainPageWrapper error={null} loading={false} title="Add Company">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Spinning up sandbox test data?{" "}
        <Link
          className="underline hover:text-gray-700 dark:hover:text-gray-300"
          to="/admin/test-company-builder"
        >
          Use the Test Company Builder
        </Link>{" "}
        instead — it auto-creates Salesforce + CARA + Auth0 users plus syncs to
        remote apps in one click.
      </p>
      <Form {...form}>
        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <Alert>
            <InfoIcon className="h-4 w-4" />
            <AlertDescription>
              Enter a Salesforce Account ID or Intacct Accounting ID to sync the
              company and its employees from Salesforce. After syncing, you'll
              be redirected to the edit page where you can configure additional
              settings.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle>Sync from Salesforce</CardTitle>
              <CardDescription>
                Import company data and employees from Salesforce
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Button
                  onClick={() => handleTypeChange("salesforceId")}
                  size="sm"
                  type="button"
                  variant={
                    identifierType === "salesforceId" ? "default" : "outline"
                  }
                >
                  Salesforce ID
                </Button>
                <Button
                  onClick={() => handleTypeChange("intacctId")}
                  size="sm"
                  type="button"
                  variant={
                    identifierType === "intacctId" ? "default" : "outline"
                  }
                >
                  Intacct ID
                </Button>
              </div>

              {identifierType === "intacctId" && (
                <FormField
                  control={form.control}
                  name="intacctId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Intacct ID</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Intacct Accounting ID (ABC123)"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {identifierType === "salesforceId" && (
                <FormField
                  control={form.control}
                  name="salesforceId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Salesforce ID</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Salesforce Account ID (0015g00000ABC123)"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button
              disabled={isLoading}
              onClick={() => navigate("/admin/companies")}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button disabled={!isValid || isLoading} type="submit">
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Syncing from Salesforce...
                </>
              ) : (
                "Sync Company"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </MainPageWrapper>
  );
};

export default CreateCompany;
